<div class="support-ticket-wrapper">
  <div class="support-ticket-shell" *ngIf="ticket && !loading && !hasError">
    <div class="ticket-page-header">
      <div>
        <h1 class="ticket-page-title">Support Ticket Details</h1>
        <p class="ticket-page-subtitle">Review the ticket summary, resolution updates, and conversation history.</p>
      </div>

      <button type="button" class="back-btn header-back-btn" (click)="back()">
        All Tickets
      </button>
    </div>

    <div class="ticket-layout">
      <aside class="ticket-sidebar">
        <section class="sidebar-card sidebar-card-emphasis">
          <span class="sidebar-label">Ticket Number</span>
          <div class="sidebar-ticket-id">{{ ticket.ticketNo }}</div>
        </section>

        <section class="sidebar-card">
          <span class="sidebar-label">Status</span>
          <span [class]="getStatusClass(ticket.status)">{{ ticket.status }}</span>
        </section>

        <section class="sidebar-card">
          <span class="sidebar-label">Created On (UTC)</span>
          <div class="info-row">
            <mat-icon>calendar_month</mat-icon>
            <span>{{ (ticket.createdAt) | date: 'dd/MM/yyyy - hh:mm a' : 'GMT' }} UTC</span>
          </div>
        </section>

        <section class="sidebar-card">
          <span class="sidebar-label">{{ getCompletionDateLabel() }}</span>
          <div class="info-row">
            <mat-icon>event_available</mat-icon>
            <span>{{ getCompletionDateValue() ? (getCompletionDateValue() | date: 'dd/MM/yyyy' : 'GMT') : '-' }}</span>
          </div>
        </section>

        <section class="sidebar-card" *ngIf="hasAttachment()">
          <span class="sidebar-label">Uploaded Attachment</span>
          <div class="attachment-card">
            <div class="attachment-left">
              <mat-icon class="pdf-icon">picture_as_pdf</mat-icon>
              <div class="attachment-meta">
                <div class="file-name">{{ getAttachmentName() }}</div>
                <div class="file-size">{{ getAttachmentSize() }}</div>
              </div>
            </div>
            <button
              mat-icon-button
              type="button"
              class="attachment-action"
              matTooltip="View PDF"
              aria-label="View uploaded PDF"
              (click)="viewAttachment()"
            >
              <mat-icon>visibility</mat-icon>
            </button>
          </div>
        </section>
      </aside>

      <section class="ticket-main-content">
        <article class="content-card">
          <div class="section-header">
            <h2>Issue</h2>
          </div>
          <p class="description-text">{{ ticket.title || '-' }}</p>
        </article>

        <article class="content-card">
          <div class="section-header">
            <h2>Description</h2>
          </div>
          <p class="description-text">{{ ticket.description }}</p>
        </article>

        <article class="content-card resolution-card" *ngIf="resolutionNotes">
          <div class="section-header">
            <h2>Resolution</h2>
          </div>
          <p class="description-text">{{ resolutionNotes }}</p>
          <div class="resolved-by" *ngIf="resolvedByName">
            Resolved by {{ resolvedByName }}
          </div>
        </article>

        <article class="content-card conversation-card">
          <div class="conversation-header">
            <h2>Conversation</h2>
            <button
              mat-icon-button
              type="button"
              class="conversation-toggle-btn"
              [matTooltip]="getConversationToggleLabel()"
              [attr.aria-label]="getConversationToggleLabel()"
              (click)="toggleConversation()"
            >
              <mat-icon>{{ getConversationToggleIcon() }}</mat-icon>
            </button>
          </div>

          <div class="conversation-thread" *ngIf="isConversationExpanded">
            <div
              *ngFor="let message of conversationMessages"
              [class]="getConversationClass(message.role)"
            >
              <div [class]="getAvatarClass(message.role)">
                {{ getInitials(message.senderName) }}
              </div>

              <div class="message-content">
                <div class="message-top">
                  <span class="sender-name">{{ message.senderName }}</span>
                  <span class="message-time">{{ message.timestamp | date: 'dd/MM/yyyy - hh:mm a' : 'GMT' }} UTC</span>
                </div>

                <div class="message-bubble">
                  {{ message.text }}
                </div>

                <div class="message-attachments" *ngIf="message.attachment">
                  <button type="button" class="attachment-pill" (click)="viewConversationAttachment(message)">
                    <mat-icon>attach_file</mat-icon>
                    <span>{{ message.attachment.name }}</span>
                    <small>{{ message.attachment.size }}</small>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="reply-section" *ngIf="isConversationExpanded">
            <span
              [matTooltip]="isReplyDisabled() ? getReplyDisabledMessage() : ''"
              [matTooltipDisabled]="!isReplyDisabled()"
            >
              <textarea
                [(ngModel)]="draftReply"
                [disabled]="isReplyDisabled()"
                [maxlength]="maxReplyLength"
                placeholder="Ask a question or provide additional details..."
              ></textarea>
            </span>

            <div class="char-counter">{{ replyLength }}/{{ maxReplyLength }}</div>

            <div class="reply-selected-file" *ngIf="selectedReplyFile">
              <mat-icon>attach_file</mat-icon>
              <span>{{ selectedReplyFile.name }}</span>
              <button
                type="button"
                class="remove-selected-file-btn"
                [disabled]="isReplySubmitting"
                (click)="clearReplyAttachment(replyFileInput)"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <div class="reply-error" *ngIf="replyError">{{ replyError }}</div>

            <div class="reply-actions">
              <div class="reply-actions-left">
                <input
                  #replyFileInput
                  type="file"
                  accept=".pdf"
                  hidden
                  (change)="onReplyFileSelected($event)"
                />
                <button
                  type="button"
                  class="attachment-btn"
                  [disabled]="isReplyDisabled() || !!selectedReplyFile || isReplySubmitting"
                  (click)="triggerReplyAttachment(replyFileInput)"
                >
                  <mat-icon>attach_file</mat-icon>
                </button>
              </div>

              <div class="reply-actions-right">
                <span
                  [matTooltip]="isReplyDisabled() ? getReplyDisabledMessage() : ''"
                  [matTooltipDisabled]="!isReplyDisabled()"
                >
                  <button
                    type="button"
                    class="send-btn"
                    [disabled]="isReplyDisabled() || !hasValidReplyContent() || isReplySubmitting"
                    (click)="sendReply(replyFileInput)"
                  >
                    {{ isReplySubmitting ? 'Sending...' : 'Send' }}
                  </button>
                </span>
              </div>
            </div>
          </div>
        </article>
      </section>
    </div>
  </div>

  <div class="state-wrapper" *ngIf="loading">
    <mat-card class="state-card">
      <mat-spinner [diameter]="36"></mat-spinner>
      <p>Loading support ticket details...</p>
    </mat-card>
  </div>

  <div class="state-wrapper" *ngIf="hasError && !loading">
    <app-error-state [message]="errorMessage"></app-error-state>
    <button type="button" class="back-btn" (click)="back()">All Tickets</button>
  </div>
</div>
